import { DEFAULT_OG_IMAGE } from "../show/showUtils.js";
import { normalizeWhitespace } from "../../utils/text.js";

const SITE_URL = "https://premierelens.com";
const MAX_TITLE_LENGTH = 65;
const MAX_DESCRIPTION_LENGTH = 155;

function getTitle(anchor = {}) {
  return normalizeWhitespace(anchor?.title || anchor?.name || "");
}

function getYear(anchor = {}) {
  if (Number.isInteger(anchor?.year)) {
    return anchor.year;
  }
  if (typeof anchor?.releaseDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(anchor.releaseDate)) {
    return Number(anchor.releaseDate.slice(0, 4));
  }
  if (typeof anchor?.premiereDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(anchor.premiereDate)) {
    return Number(anchor.premiereDate.slice(0, 4));
  }
  return null;
}

function truncateToFit(prefix, title, suffix, maxLength) {
  const available = maxLength - prefix.length - suffix.length;
  if (available <= 0) {
    return `${prefix}${suffix}`.slice(0, maxLength);
  }
  const truncated = normalizeWhitespace(title).slice(0, available).replace(/\s+\S*$/, "").trim();
  return `${prefix}${truncated}${suffix}`;
}

function buildShowsLikeTitle(anchor = {}, picks = []) {
  const title = getTitle(anchor);
  if (!title) {
    return "";
  }

  const pickCount = Array.isArray(picks) && picks.length > 0 ? picks.length : 3;
  const year = getYear(anchor);
  const prefix = `${pickCount} Shows Like `;
  const withYear = `${prefix}${title}${year ? ` (${year})` : ""} to Watch Next | PremiereLens`;
  if (withYear.length <= MAX_TITLE_LENGTH) {
    return withYear;
  }

  const withoutYear = `${prefix}${title} to Watch Next | PremiereLens`;
  if (withoutYear.length <= MAX_TITLE_LENGTH) {
    return withoutYear;
  }

  const concise = `${prefix}${title} | PremiereLens`;
  if (concise.length <= MAX_TITLE_LENGTH) {
    return concise;
  }

  return truncateToFit(prefix, title, " | PremiereLens", MAX_TITLE_LENGTH);
}

function pickTasteTags(anchor = {}) {
  return Array.isArray(anchor?.tasteTags)
    ? anchor.tasteTags.filter((tag) => typeof tag === "string" && tag.trim()).map((tag) => tag.trim())
    : [];
}

function buildShowsLikeDescription({ anchor = {}, picks = [] } = {}) {
  const title = getTitle(anchor);
  const tasteTags = pickTasteTags(anchor);
  if (!title || tasteTags.length === 0) {
    return "";
  }

  const [firstTag, secondTag] = tasteTags;
  const tagClause = secondTag
    ? `${firstTag} and ${secondTag}`
    : `${firstTag}`;
  const pickCount = Array.isArray(picks) && picks.length > 0 ? picks.length : 3;
  // Picks come from the premiere catalogue: new series and returning seasons.
  const matchSuffix = "all recent premieres or returning seasons";
  const base = secondTag
    ? `Looking for what to watch after ${title}? ${pickCount} curated picks that share its ${tagClause} vibes, ${matchSuffix}.`
    : `Looking for what to watch after ${title}? ${pickCount} curated picks that share its ${tagClause} vibe, ${matchSuffix}.`;

  if (base.length <= MAX_DESCRIPTION_LENGTH) {
    return base;
  }

  const shorter = secondTag
    ? `After ${title}, ${pickCount} curated picks share its ${tagClause} vibes, ${matchSuffix}.`
    : `After ${title}, ${pickCount} curated picks share its ${tagClause} vibe, ${matchSuffix}.`;

  if (shorter.length <= MAX_DESCRIPTION_LENGTH) {
    return shorter;
  }

  return normalizeWhitespace(shorter).slice(0, MAX_DESCRIPTION_LENGTH - 1).replace(/\s+\S*$/, "").trimEnd() + "…";
}

function getSeoImage(anchor = {}) {
  return anchor?.posterMetadata?.thumbnail || anchor?.poster || DEFAULT_OG_IMAGE;
}

export function buildShowsLikeSeoMeta({ anchor = {}, slug = "", picks = [] } = {}) {
  const title = buildShowsLikeTitle(anchor, picks);
  const description = buildShowsLikeDescription({ anchor, picks });
  if (!title || !description) {
    return null;
  }

  return {
    title,
    description,
    pathname: `/shows-like/${slug}`,
    canonicalUrl: `${SITE_URL}/shows-like/${slug}`,
    ogUrl: `${SITE_URL}/shows-like/${slug}`,
    ogType: "website",
    image: getSeoImage(anchor),
    robots: "index,follow",
  };
}
